"use client";

import { useState, type FormEvent } from "react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { BOTON_PRIMARIO, CAMPO } from "@/components/shared/estilos";
import { getErrorCode, type BackendErrorEnvelope } from "@/lib/errors";
import { getCurrentUser } from "@/lib/auth";
import { listAccessibleOrganizations } from "@/lib/organizations";
import { setCurrentOrganizationCookie } from "@/lib/tenant-context";

import { ENLACE_TACTIL } from "./estilosAcceso";
import Spinner from "./Spinner";

// Relative URL — browser requests go through the Next.js rewrite proxy (no CORS).
const API_URL = "";

// El campo de siempre; el borde rojo por `aria-invalid`, que va detrás en la
// hoja de estilos y gana al `border-line-strong` de `CAMPO`.
const CAMPO_FORM = [
  CAMPO,
  "aria-[invalid=true]:border-red-500 aria-[invalid=true]:bg-red-50",
].join(" ");

interface FieldErrors {
  email?: string;
  password?: string;
  two_factor_code?: string;
}

interface LoginContrasenaFormProps {
  /** El correo que ya se escribió en S1, para no pedirlo dos veces. */
  emailInicial?: string;
  /**
   * Un error que trae la URL al llegar (p. ej. `?error=google_fallo` cuando
   * Google devuelve aquí a alguien del equipo de la plataforma, que solo
   * entra con contraseña). Se pinta como el de la API y se va al enviar.
   */
  errorInicial?: string | null;
}

/**
 * El login con contraseña de siempre (UI spec 003, S1-d), sacado tal cual de
 * `app/(auth)/login/page.tsx`: mismo endpoint, misma validación, mismo paso de
 * 2FA y mismo destino. Solo cambian las clases, que pasan a `estilos.ts`, y
 * los enlaces de debajo, que pone `AccesoClient`.
 *
 * Responsive: una columna a todo lo ancho de la tarjeta de `(auth)`, que es
 * quien fija el ancho; desde `sm` solo se abre el aire entre campos.
 */
export default function LoginContrasenaForm({
  emailInicial = "",
  errorInicial = null,
}: LoginContrasenaFormProps) {
  const t = useTranslations("auth.login");
  const tErrors = useTranslations("errors");

  const searchParams = useSearchParams();

  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState(emailInicial);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState<string | null>(errorInicial);
  const [isLoading, setIsLoading] = useState(false);

  const emailErrorId = "email-error";
  const passwordErrorId = "password-error";
  const twoFactorErrorId = "two-factor-code-error";
  const apiErrorId = "api-error";

  function validateFields(emailVal: string, passwordVal: string): FieldErrors {
    const errors: FieldErrors = {};
    if (!emailVal) {
      errors.email = t("validation.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      errors.email = t("validation.emailInvalid");
    }
    if (!passwordVal) {
      errors.password = t("validation.passwordRequired");
    }
    return errors;
  }

  function handleBack(): void {
    setStep(1);
    setTwoFactorCode("");
    setApiError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setApiError(null);

    // Only run field validation on step 1
    if (step === 1) {
      const errors = validateFields(email, password);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          two_factor_code: twoFactorCode.trim() || undefined,
        }),
      });

      if (res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          must_reset_password?: boolean;
        };
        try {
          const organizations = await listAccessibleOrganizations();
          const firstOrganizationId = organizations[0]?.id;
          if (firstOrganizationId) {
            setCurrentOrganizationCookie(firstOrganizationId);
          }
        } catch {
          // Best effort only. Fallback redirect below still works for
          // admin/receptionist and keeps the login flow resilient.
        }

        const redirectParam = searchParams.get("redirect");
        const currentUser = await getCurrentUser();
        const defaultTarget =
          payload.must_reset_password === true || currentUser?.must_reset_password
            ? "/force-password-reset"
            : currentUser?.role === "support"
              ? "/support"
              : "/dashboard";
        // Sólo rutas propias, enteras: `//evil.com` y `/\evil.com` también
        // empiezan por «/» y el navegador los trata como otro origen, y un
        // espacio o salto de línea en medio no es una ruta (misma regla que
        // `cargarAcceso.ts`).
        const target =
          redirectParam && /^\/(?![/\\])[^\s]*$/.test(redirectParam)
            ? redirectParam
            : defaultTarget;
        // Force a full document navigation so the next request sees the fresh
        // HttpOnly session cookie set by the login response.
        window.location.assign(target);
        return;
      }

      const body = (await res.json().catch(() => ({}))) as BackendErrorEnvelope;

      if (res.status === 401) {
        const code = getErrorCode(body);
        if (code === "TWO_FACTOR_REQUIRED") {
          // Transition to step 2 — do not show an error message
          setStep(2);
          setApiError(null);
        } else {
          setApiError(t("errors.invalidCredentials"));
        }
      } else if (res.status === 403) {
        setApiError(t("errors.suspended"));
      } else {
        // Use structured error_code if available, otherwise generic
        const code = getErrorCode(body);
        setApiError(
          code !== "UNKNOWN_ERROR" ? tErrors(code) : t("errors.generic")
        );
      }
    } catch {
      setApiError(t("errors.connectionFailed"));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      {apiError && (
        <div
          id={apiErrorId}
          role="alert"
          className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {apiError}
        </div>
      )}

      {step === 1 && (
        <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-5">
          {/* Email */}
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-zinc-700">
              {t("emailLabel")}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={fieldErrors.email ? emailErrorId : undefined}
              aria-invalid={!!fieldErrors.email}
              disabled={isLoading}
              className={CAMPO_FORM}
            />
            {fieldErrors.email && (
              <p id={emailErrorId} className="mt-1.5 text-sm text-red-700">
                {fieldErrors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-zinc-700">
              {t("passwordLabel")}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby={fieldErrors.password ? passwordErrorId : undefined}
                aria-invalid={!!fieldErrors.password}
                disabled={isLoading}
                className={[CAMPO_FORM, "pr-12"].join(" ")}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-zinc-500 hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus"
                aria-label={showPassword ? t("hidePassword") : t("showPassword")}
                tabIndex={0}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {fieldErrors.password && (
              <p id={passwordErrorId} className="mt-1.5 text-sm text-red-700">
                {fieldErrors.password}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            aria-disabled={isLoading}
            className={[BOTON_PRIMARIO, "w-full gap-2"].join(" ")}
          >
            {isLoading && <Spinner />}
            {isLoading ? t("submittingButton") : t("submitButton")}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900">{t("twoFactor.title")}</h2>
            <p className="mt-1 text-sm text-zinc-500">{t("twoFactor.subtitle")}</p>
          </div>

          {/* 2FA code input */}
          <div>
            <label
              htmlFor="two-factor-code"
              className="mb-1.5 block text-sm font-medium text-zinc-700"
            >
              {t("twoFactor.title")}
            </label>
            <input
              id="two-factor-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              aria-describedby={fieldErrors.two_factor_code ? twoFactorErrorId : undefined}
              aria-invalid={!!fieldErrors.two_factor_code}
              disabled={isLoading}
              autoFocus
              className={CAMPO_FORM}
              placeholder="123456"
            />
            {fieldErrors.two_factor_code && (
              <p id={twoFactorErrorId} className="mt-1.5 text-sm text-red-700">
                {fieldErrors.two_factor_code}
              </p>
            )}
          </div>

          {/* Verify button */}
          <button
            type="submit"
            disabled={isLoading}
            aria-disabled={isLoading}
            className={[BOTON_PRIMARIO, "w-full gap-2"].join(" ")}
          >
            {isLoading && <Spinner />}
            {t("twoFactor.verifyButton")}
          </button>

          {/* Back link */}
          <div className="text-center">
            <button type="button" onClick={handleBack} className={ENLACE_TACTIL}>
              {t("twoFactor.back")}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

/* ── Icon components ──────────────────────────────────────────────────────── */

function EyeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
